/**
 * AsyncGuard — renders loading skeleton, error banner with retry, or children.
 *
 * Replaces the manual `if (loading) return <Spinner />` + silent error
 * swallowing pattern across every data-fetching page.
 *
 * Usage:
 *   <AsyncGuard loading={loading} error={error} retry={retry}>
 *     {children}
 *   </AsyncGuard>
 *
 * Or with the skeleton variant for list pages:
 *   <AsyncGuard loading={loading} error={error} retry={retry} skeleton="table">
 *     {children}
 *   </AsyncGuard>
 */

import type { ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { SkeletonDashboard, SkeletonTable, SkeletonCard } from './Skeleton';

interface AsyncGuardProps {
  loading: boolean;
  error: string | null;
  retry?: () => void;
  /** Which skeleton to show while loading. Default: "spinner". */
  skeleton?: 'spinner' | 'dashboard' | 'table' | 'card' | 'none';
  children: ReactNode;
}

/**
 * Bare spinning-ring glyph. Compose into custom layouts (full-screen splash,
 * dropdown panel, status card). For a standard centered page/section loader
 * use <Spinner/>, which wraps this in a padded flex container.
 *
 * `className` controls dimensions + border weight + layout (mx-auto, mb-4).
 * Default is the common w-8 h-8 border-4. This de-duplicates the spinner
 * glyph (rounded-full + border-primary-600 + border-t-transparent +
 * animate-spin) that was hand-rolled across ~15 files.
 */
export function SpinnerRing({ className = 'w-8 h-8 border-4' }: { className?: string }) {
  return (
    <div className={`rounded-full border-primary-600 border-t-transparent animate-spin ${className}`} />
  );
}

/**
 * Centered content loader — the standard "if (loading) return <Spinner/>" for
 * a page or section. `className` overrides the vertical padding (default py-20;
 * pass "py-12" for tighter sections).
 */
export function Spinner({ className = 'py-20' }: { className?: string }) {
  return (
    <div className={`flex justify-center ${className}`}>
      <SpinnerRing />
    </div>
  );
}

export function AsyncGuard({
  loading,
  error,
  retry,
  skeleton = 'spinner',
  children,
}: AsyncGuardProps) {
  if (loading) {
    switch (skeleton) {
      case 'dashboard':
        return <SkeletonDashboard />;
      case 'table':
        return <SkeletonTable rows={5} />;
      case 'card':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        );
      case 'none':
        return null;
      default:
        return <Spinner />;
    }
  }

  if (error) {
    return (
      <Card padding="lg" className="text-center py-10">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <p className="text-slate-700 dark:text-slate-300 font-medium mb-1">
          Something went wrong
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 max-w-md mx-auto">
          {error}
        </p>
        {retry && (
          <Button variant="secondary" size="sm" onClick={retry} className="gap-2">
            <RefreshCw className="w-4 h-4" /> Retry
          </Button>
        )}
      </Card>
    );
  }

  return <>{children}</>;
}

/**
 * Inline error banner for partial failures (e.g., Dashboard where 3/4 calls
 * succeed but 1 fails). Shows at the top of the page, doesn't replace content.
 */
export function ErrorBanner({
  message,
  retry,
}: {
  message: string;
  retry?: () => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-[var(--radius-lg)] border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-sm">
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span className="flex-1">{message}</span>
      {retry && (
        <button
          onClick={retry}
          className="shrink-0 font-medium underline hover:no-underline"
        >
          Retry
        </button>
      )}
    </div>
  );
}
